import * as React from "react";

import {apiArray}   from "@Common/logic/api-array";
import {cs}         from "@Common/react/chain-services";
import {plural}     from "@Common/ui-components/language/plural";
import {cascadeSum} from "@Common/utils/cascade";
import {chain}      from "@Common/utils/fs";

import {SummaryTable} from "../../common/summary-table/summary-table";

export const CartSummaryTable = ({cart, items, billingTotal, balance, creditCards, creditAttrs}) => cs(
    ({}) => SummaryTable({
        title: "Cart Summary",
        subtitle: chain(
            cart?.items?.length,
            (count) => `Your cart contains ${count ?? 0} ${plural("item", count)}.`,
        ),
        lines: [
            {
                label: "Subtotal",
                amount: cascadeSum(items, "[*].itemTotals[*]", (it) => it.type === "subTotal" ? it.amount : 0),
            },

            (() => {
                const promoCodeAmount = cascadeSum(items, "[*].itemTotals[*]", (it) => it.type === "promoCode" ? it.amount : 0);
                return promoCodeAmount && {
                    label: "Promo Code",
                    amount: promoCodeAmount,
                    isCredit: true,
                };
            })(),

            (() => {
                const shippingCharge = cascadeSum(items, "[*].itemTotals[*]", (it) => it.type === "shippingCharge" ? it.amount : 0);
                const shippingItemsCount = items.filter((i) => i.selections.distributionType === "shipping").length;
                return shippingItemsCount && {
                    label: "Shipping",
                    amount: shippingCharge,
                };
            })(),

            (() => {
                const handlingFee = cascadeSum(items, "[*].itemTotals[*]", (it) => it.type === "handlingFee" ? it.amount : 0);
                return handlingFee && {
                    label: "Handling Fee",
                    amount: handlingFee,
                }
            })(),

            (() => {
                const vatTax = cascadeSum(items, "[*].itemTotals[*]", (it) => it.type === "vatTax" ? it.amount : 0);
                const importTax = cascadeSum(items, "[*].itemTotals[*]", (it) => it.type === "importTax" ? it.amount : 0);
                return !(vatTax > 0 || importTax > 0) && {
                    label: "Sales Tax",
                    amount: cascadeSum(items, "[*].itemTotals[*]", (it) => it.type === "salesTax" ? it.amount : 0),
                }
            })(),

            (() => {
                const rdfFee = cascadeSum(items, "[*].itemTotals[*]", (it) => it.type === "rdf" ? it.amount : 0);
                const rdfName = rdfFee > 0 ? items.map((it) => it.itemTotals).flat().find((it) => it.type === "rdf")?.name : '';
                const rdfDescription = rdfFee > 0 ? items.map((it) => it.itemTotals).flat().find((it) => it.type === "rdf")?.description : '';
                return rdfFee && {
                    label: rdfName,
                    amount: rdfFee,
                    description: rdfDescription,
                }
            })(),

            (() => {
                const vatTax = cascadeSum(items, "[*].itemTotals[*]", (it) => it.type === "vatTax" ? it.amount : 0);
                const importTax = cascadeSum(items, "[*].itemTotals[*]", (it) => it.type === "importTax" ? it.amount : 0);
                const vatDescription = vatTax > 0 ? items.map((it) => it.itemTotals).flat().find((it) => it.type === "vatTax")?.description : '';
                return (vatTax > 0 || importTax > 0) && {
                    label: "VAT/GST",
                    amount: vatTax,
                    description: vatDescription,
                }
            })(),

            (() => {
                const vatTax = cascadeSum(items, "[*].itemTotals[*]", (it) => it.type === "vatTax" ? it.amount : 0);
                const importTax = cascadeSum(items, "[*].itemTotals[*]", (it) => it.type === "importTax" ? it.amount : 0);
                return (vatTax > 0 || importTax > 0) && {
                    label: "Import Duties",
                    amount: importTax,
                }
            })(),
            ...creditAttrs,

        ].filter(v=>v),
        balance,

        explanations: [
            (() => {
                return billingTotal && {
                    label: apiArray(cart.appliedPayments.billing)[0].name,
                    amount: billingTotal,
                };
            })(),
            ...creditCards?.map((creditCard) => (
                {
                    label: `Charge to Card ${creditCard.cardLast4}`,
                    amount: creditCard.amount,
                }
            )) ?? [],
        ].filter(v=>v),
    }),
);

